import { FileChecksum } from '@rails/activestorage/src/file_checksum';
import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { UploadImageResponse } from '~/services/api/community/types';

const COMMUNITY_UPLOAD_PATH = '/community_upload';

const isUploadImageResponse = (value: unknown): value is UploadImageResponse => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as { key?: unknown; direct_upload?: unknown; public_url?: unknown };
  if (typeof v.key !== 'string' || typeof v.public_url !== 'string') return false;
  if (typeof v.direct_upload !== 'object' || v.direct_upload === null) return false;
  const du = v.direct_upload as { url?: unknown; headers?: unknown };
  return typeof du.url === 'string' && typeof du.headers === 'object' && du.headers !== null;
};

const computeBlobChecksum = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    FileChecksum.create(file, (err: Error | null, checksum: string) => {
      if (err) reject(err);
      else resolve(checksum);
    });
  });

const useUploadCommunityImage = () => {
  const ensureCsrfToken = useEnsureCsrfToken();

  return useMutation({
    mutationFn: async (file: File): Promise<UploadImageResponse> => {
      const csrfToken = await ensureCsrfToken();
      const checksum = await computeBlobChecksum(file);
      const presignedRes = await fetch(COMMUNITY_UPLOAD_PATH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body: JSON.stringify({
          blob: {
            filename: file.name,
            byte_size: file.size,
            checksum,
            content_type: file.type,
          },
        }),
      });
      if (!presignedRes.ok) throw new Error('upload_failed');
      const data: unknown = await presignedRes.json();
      if (!isUploadImageResponse(data)) throw new Error('upload_failed');

      const s3Res = await fetch(data.direct_upload.url, {
        method: 'PUT',
        headers: data.direct_upload.headers,
        body: file,
      });
      if (!s3Res.ok) throw new Error('s3_upload_failed');

      return data;
    },
  });
};

export default useUploadCommunityImage;
